import {
  INTEGRATIONS_FETCH_STATUS_REQUEST,
  INTEGRATIONS_FETCH_STATUS_SUCCESS,
  INTEGRATIONS_FETCH_STATUS_FAILURE,
  INTEGRATIONS_DISCONNECT_REQUEST,
  INTEGRATIONS_DISCONNECT_SUCCESS,
  INTEGRATIONS_DISCONNECT_FAILURE,
  INTEGRATIONS_SET_CONNECTION_STATUS,
  INTEGRATIONS_FETCH_EMAILS_REQUEST,
  INTEGRATIONS_FETCH_EMAILS_SUCCESS,
  INTEGRATIONS_FETCH_EMAILS_FAILURE,
  INTEGRATIONS_FETCH_MORE_EMAILS_REQUEST,
  INTEGRATIONS_FETCH_MORE_EMAILS_SUCCESS,
  INTEGRATIONS_FETCH_MORE_EMAILS_FAILURE,
  INTEGRATIONS_FETCH_FOLDER_COUNTS_SUCCESS,
  INTEGRATIONS_FETCH_EMAIL_DETAIL_REQUEST,
  INTEGRATIONS_FETCH_EMAIL_DETAIL_SUCCESS,
  INTEGRATIONS_FETCH_EMAIL_DETAIL_FAILURE,
  INTEGRATIONS_CLEAR_EMAIL_DETAIL,
  INTEGRATIONS_MARK_EMAIL_READ,
  INTEGRATIONS_RESET_STATE,
} from '@core/constants/actions';
import {
  deleteConnection,
  getConnectedEmails,
  getConnectedEmailDetail,
  getConnections,
  getMailFolderCounts,
  IntegrationConnectionStatus,
  IntegrationProvider,
} from '@core/api/integrations/integrationsApi';
import { AulaThunkAction } from '@core/types/state';

export const isConnectionNotFoundError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;

  const axiosErr = err as {
    response?: {
      status?: number;
      data?:
        | Array<{ code?: string; status?: number; detail?: string }>
        | {
            errors?: Array<{ code?: string; status?: number; detail?: string }>;
            code?: string;
            detail?: string;
            message?: string;
          };
    };
    code?: string;
    message?: string;
  };

  const status = axiosErr.response?.status;
  const data = axiosErr.response?.data;

  if (status === 404) {
    return true;
  }

  if (Array.isArray(data)) {
    return data.some(
      (e) =>
        e.code === 'connection_not_found' ||
        e.status === 404 ||
        e.detail?.toLowerCase().includes('connection not found')
    );
  }

  if (data && typeof data === 'object') {
    if (data.code === 'connection_not_found') return true;
    if (
      data.detail?.toLowerCase().includes('connection not found') ||
      data.message?.toLowerCase().includes('connection not found')
    ) {
      return true;
    }
    if (Array.isArray(data.errors)) {
      return data.errors.some(
        (e) =>
          e.code === 'connection_not_found' ||
          e.status === 404 ||
          e.detail?.toLowerCase().includes('connection not found')
      );
    }
  }

  return false;
};

let lastFolderCountsFetchTime = 0;
const FOLDER_COUNTS_TTL_MS = 60_000;

export const resetConnectedIntegrationState =
  (): AulaThunkAction<void> => (dispatch) => {
    lastFolderCountsFetchTime = 0;
    dispatch({ type: INTEGRATIONS_RESET_STATE });
  };

export const fetchConnectedIntegrationStatus =
  (
    provider: IntegrationProvider,
    options?: { force?: boolean }
  ): AulaThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const currentConnection = getState().integrations?.connection;
    if (!options?.force && currentConnection && !currentConnection.isLoading) {
      return;
    }

    dispatch({ type: INTEGRATIONS_FETCH_STATUS_REQUEST });
    try {
      const { connections } = await getConnections();
      const match = connections.find((c) => c.provider === provider);
      if (!match || match.status === 'revoked') {
        dispatch({
          type: INTEGRATIONS_FETCH_STATUS_SUCCESS,
          status: 'none',
          accountEmail: null,
        });
        return;
      }
      dispatch({
        type: INTEGRATIONS_FETCH_STATUS_SUCCESS,
        status: match.status,
        accountEmail: match.accountEmail,
      });
    } catch (err: any) {
      if (isConnectionNotFoundError(err)) {
        dispatch(resetConnectedIntegrationState());
        return;
      }
      const errorCode =
        err?.response?.data?.errors?.[0]?.code || 'connect_failed';
      dispatch({
        type: INTEGRATIONS_FETCH_STATUS_FAILURE,
        error: errorCode,
      });
    }
  };

export const setConnectedIntegrationStatus =
  (
    status: IntegrationConnectionStatus | 'none',
    accountEmail?: string | null
  ): AulaThunkAction<void> =>
  (dispatch) => {
    dispatch({
      type: INTEGRATIONS_SET_CONNECTION_STATUS,
      status,
      accountEmail,
    });
  };

export const disconnectConnectedIntegration =
  (provider: IntegrationProvider): AulaThunkAction<Promise<void>> =>
  async (dispatch) => {
    dispatch({ type: INTEGRATIONS_DISCONNECT_REQUEST });
    try {
      await deleteConnection(provider);
      lastFolderCountsFetchTime = 0;
      dispatch({ type: INTEGRATIONS_DISCONNECT_SUCCESS });
    } catch (err: any) {
      const errorCode =
        err?.response?.data?.errors?.[0]?.code || 'disconnect_failed';
      dispatch({
        type: INTEGRATIONS_DISCONNECT_FAILURE,
        error: errorCode,
      });
      throw err;
    }
  };

export const fetchConnectedEmails =
  (
    provider: IntegrationProvider,
    options?: { folder?: string; top?: number; force?: boolean }
  ): AulaThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const emailsState = getState().integrations?.emails;
    if (
      !options?.force &&
      emailsState &&
      emailsState.items.length > 0 &&
      !emailsState.error
    ) {
      return;
    }

    dispatch({ type: INTEGRATIONS_FETCH_EMAILS_REQUEST });
    try {
      const { emails, nextLink } = await getConnectedEmails(provider, {
        folder: options?.folder,
        top: options?.top,
      });
      dispatch({
        type: INTEGRATIONS_FETCH_EMAILS_SUCCESS,
        emails,
        nextLink,
      });
    } catch (err: any) {
      if (isConnectionNotFoundError(err)) {
        dispatch(resetConnectedIntegrationState());
        return;
      }
      dispatch({
        type: INTEGRATIONS_FETCH_EMAILS_FAILURE,
        error: err?.message || 'Failed to fetch connected emails',
      });
    }
  };

export const fetchMoreConnectedEmails =
  (provider: IntegrationProvider): AulaThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const emailsState = getState().integrations?.emails;
    const nextLink = emailsState?.nextLink;
    if (!nextLink || emailsState?.isLoadingMore) {
      return;
    }

    dispatch({ type: INTEGRATIONS_FETCH_MORE_EMAILS_REQUEST });
    try {
      const { emails, nextLink: newNextLink } = await getConnectedEmails(
        provider,
        { nextLink }
      );
      dispatch({
        type: INTEGRATIONS_FETCH_MORE_EMAILS_SUCCESS,
        emails,
        nextLink: newNextLink,
      });
    } catch (err: any) {
      if (isConnectionNotFoundError(err)) {
        dispatch(resetConnectedIntegrationState());
        return;
      }
      dispatch({
        type: INTEGRATIONS_FETCH_MORE_EMAILS_FAILURE,
        error: err?.message || 'Failed to load more emails',
      });
    }
  };

export const fetchConnectedMailFolderCounts =
  (
    provider: IntegrationProvider,
    folder: string,
    options?: { force?: boolean }
  ): AulaThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const now = Date.now();
    const emailsState = getState().integrations?.emails;
    if (
      !options?.force &&
      emailsState &&
      emailsState.items.length > 0 &&
      now - lastFolderCountsFetchTime < FOLDER_COUNTS_TTL_MS
    ) {
      return;
    }

    try {
      const data = await getMailFolderCounts(provider, folder);
      lastFolderCountsFetchTime = Date.now();
      dispatch({
        type: INTEGRATIONS_FETCH_FOLDER_COUNTS_SUCCESS,
        unreadCount: data.unread,
      });
    } catch (err: any) {
      if (isConnectionNotFoundError(err)) {
        dispatch(resetConnectedIntegrationState());
      }
      // Silently fail folder count fetching otherwise
    }
  };

let activeEmailDetailFetchId: string | null = null;

export const fetchConnectedEmailDetail =
  (
    provider: IntegrationProvider,
    id: string,
    options?: { force?: boolean }
  ): AulaThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const detailState = getState().integrations?.emailDetail;
    if (!options?.force) {
      if (detailState?.detail?.id === id) {
        return;
      }
      if (detailState?.isLoading && activeEmailDetailFetchId === id) {
        return;
      }
    }

    activeEmailDetailFetchId = id;
    dispatch({ type: INTEGRATIONS_FETCH_EMAIL_DETAIL_REQUEST });
    try {
      const { email } = await getConnectedEmailDetail(provider, id);
      if (activeEmailDetailFetchId !== id) {
        return;
      }
      dispatch({
        type: INTEGRATIONS_FETCH_EMAIL_DETAIL_SUCCESS,
        email,
      });
    } catch (err: any) {
      if (activeEmailDetailFetchId !== id) {
        return;
      }
      if (isConnectionNotFoundError(err)) {
        dispatch(resetConnectedIntegrationState());
        return;
      }
      dispatch({
        type: INTEGRATIONS_FETCH_EMAIL_DETAIL_FAILURE,
        error: err?.message || 'Failed to fetch email detail',
      });
    }
  };

export const clearConnectedEmailDetail =
  (): AulaThunkAction<void> => (dispatch) => {
    activeEmailDetailFetchId = null;
    dispatch({ type: INTEGRATIONS_CLEAR_EMAIL_DETAIL });
  };

export const markConnectedEmailRead =
  (emailId: string): AulaThunkAction<void> =>
  (dispatch) => {
    dispatch({
      type: INTEGRATIONS_MARK_EMAIL_READ,
      emailId,
    });
  };
